import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import type { CustomerDetail, CustomerSales } from '@/lib/api/types'
import { errorMessage } from '@/lib/api/errors'
import { formatDate, formatUsd } from '@/lib/format'
import { useSetInternal } from './api'

const tiers: Record<
  CustomerSales['tier'],
  { label: string; tone: 'accent' | 'success' | 'neutral' | 'warning' | 'outline' }
> = {
  loyal: { label: 'Loyal (12+ months)', tone: 'accent' },
  regular: { label: 'Regular', tone: 'success' },
  new: { label: 'New customer', tone: 'neutral' },
  lapsed: { label: 'Lapsed', tone: 'warning' },
  never: { label: 'Never paid', tone: 'outline' },
}

const actionLabels: Record<string, string> = {
  'billing.plan_changed': 'Plan changed',
  'billing.plan_change_scheduled': 'Plan change scheduled',
  'billing.plan_change_unscheduled': 'Scheduled change dropped',
  'billing.cancelled': 'Cancelled',
  'billing.resumed': 'Resumed',
  'billing.subscription_refunded': 'Refunded: ended at once',
  'billing.subscription_paid_after_refund': 'Paid again after a refund',
  'billing.addon_changed': 'Add-on changed',
  'billing.subscription_created': 'Subscribed',
  'billing.subscription_updated': 'Subscription updated by Lemon Squeezy',
  'billing.checkout_started': 'Checkout opened',
  'billing.lifetime_withdrawn': 'Lifetime purchase withdrawn (14-day refund)',
}

/** What this customer has paid, for loyalty offers and discounts. */
export function SalesCard({ customer: c, canManage }: { customer: CustomerDetail; canManage: boolean }) {
  const setInternal = useSetInternal(c.id)
  const s = c.sales
  const tier = tiers[s.tier]
  const max = Math.max(1, ...s.by_month.map((m) => m.paid))

  return (
    <Card>
      <CardHeader
        title="Sales"
        description="From Lemon Squeezy's payments, in USD: subscriptions, lifetime plans and top-ups. MRR counts subscriptions only."
        actions={<Badge tone={tier.tone}>{tier.label}</Badge>}
      />
      <CardBody className="grid gap-4 text-[13px]">
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {/* "Total": subscriptions plus one-time payments (lifetime plans and top-ups). */}
          <Stat label="Total paid" value={formatUsd(s.total_paid ?? s.lifetime_paid)} />
          <Stat label="Total net, after tax and refunds" value={formatUsd(s.total_net ?? s.lifetime_net)} />
          <Stat
            label="Paying now"
            value={s.mrr > 0 ? `${formatUsd(s.mrr)}/month` : s.paying ? 'Lifetime plan' : 'No'}
          />
          <Stat label="Months paying" value={String(s.months_paying)} />
          <Stat label="Payments" value={String(s.payments)} />
          <Stat label="Discounts used" value={formatUsd(s.lifetime_discounts)} />
          {(s.one_time_paid?.lifetime ?? 0) > 0 && (
            <Stat label="Lifetime plans, paid once" value={formatUsd(s.one_time_paid?.lifetime ?? 0)} />
          )}
          {(s.one_time_paid?.top_ups ?? 0) > 0 && (
            <Stat label="Top-ups, paid once" value={formatUsd(s.one_time_paid?.top_ups ?? 0)} />
          )}
        </dl>
        {s.first_paid_at && (
          <p className="text-xs text-ink-subtle">
            First payment {formatDate(s.first_paid_at)} · latest {formatDate(s.last_paid_at)}
            {s.lifetime_refunded > 0 && ` · ${formatUsd(s.lifetime_refunded)} refunded`}
          </p>
        )}
        <div>
          <p className="mb-2 text-xs text-ink-subtle">Paid each month (last 12)</p>
          <div className="flex h-16 items-end gap-1" role="img" aria-label="Payments in the last 12 months">
            {s.by_month.map((m) => (
              <div
                key={m.month}
                title={`${m.month}: ${formatUsd(m.paid)}`}
                className="flex-1 rounded-t-sm bg-accent/70"
                style={{
                  height: `${Math.max(m.paid > 0 ? 8 : 2, (m.paid / max) * 100)}%`,
                  opacity: m.paid > 0 ? 1 : 0.35,
                }}
              />
            ))}
          </div>
          <table className="sr-only">
            <caption>Paid each month</caption>
            <tbody>
              {s.by_month.map((m) => (
                <tr key={m.month}>
                  <th scope="row">{m.month}</th>
                  <td>{formatUsd(m.paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {s.history.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs text-ink-subtle">Plan history</p>
            <ul className="grid gap-1 text-xs">
              {s.history.map((h, i) => (
                <li key={`${h.created_at}-${i}`} className="flex justify-between gap-3">
                  <span className="text-ink">
                    {actionLabels[h.action] ?? h.action.replace('billing.', '').replace(/_/g, ' ')}
                    {typeof h.properties?.by_arvaseo_staff === 'string' && (
                      <span className="text-ink-subtle"> · by {h.properties.by_arvaseo_staff}</span>
                    )}
                  </span>
                  <span className="text-ink-subtle">{formatDate(h.created_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardBody>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3 text-xs">
        <p className="max-w-md text-ink-subtle">
          {c.internal_account
            ? 'Internal account: everything stays here, but it’s left out of the dashboard, its charts, Sales, Demographics, Referrals and the customer list (unless you show internal accounts).'
            : 'Your own or a test workspace? Mark it internal to leave it out of the dashboard, revenue and the other figures.'}
        </p>
        {canManage && (
          <Button
            variant="secondary"
            size="sm"
            loading={setInternal.isPending}
            onClick={() =>
              setInternal.mutate(!c.internal_account, {
                onSuccess: (updated) =>
                  toast.success(
                    updated.internal_account
                      ? `${c.name} is internal: left out of the figures and the customer list.`
                      : `${c.name} counts in the figures again.`,
                  ),
                onError: (e) => toast.error(errorMessage(e)),
              })
            }
          >
            {c.internal_account ? 'Count it again' : 'Mark as internal'}
          </Button>
        )}
      </div>
    </Card>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-surface-sunken px-3 py-2">
      <dt className="text-xs text-ink-subtle">{label}</dt>
      <dd className="font-semibold text-ink">{value}</dd>
    </div>
  )
}
